import omit from 'lodash/omit'
import { context, updateContextState, request } from '@tools/wr-catalog-base'
import { getDuration } from '../product-utils'
import { buildHtml as spBuildHtml } from './SuccessPathGenerator'
import { buildHtml as ssBuildHtml } from './SuccessServicesGenerator'
import generateDefaultSOW from './DefaultGenerator'
import { fetchImage } from '../html'

// Convert these patterns to units that libreoffice can deal with
const unitReplacements = {
  '\\d+\\.*\\d*r?em': (units) => `${parseFloat(units) * 16}px`,
  '\\d+\\.*\\d*\\%': () => 'auto',
}
const translateUnits = (_s) => {
  let s = _s
  Object.entries(unitReplacements).forEach(([k, v]) => {
    const checker = new RegExp(k, 'g')
    s = s.replace(checker, v)
  })

  return s
}

const cssRulesToArray = (s) => {
  const result = []
  for (const k of s) {
    result.push([k, s.getPropertyValue(k)])
  }
  return result
}
const buildRule = (ruleName, ruleValue) => `${ruleName}:${ruleValue}`
const blacklistedProps = new Set(['opacity'])
const transformRule = (ruleName, ruleValue, elm, cssVars, computedStyles) => {
  if (blacklistedProps.has(ruleName)) {
    return
  }

  // Bail out of dealing with huge values (like data URIs)
  if (ruleValue.length > 1000) {
    return buildRule(ruleName, ruleValue)
  }

  // Remove vendor-specific rules for better cross-browser consistency
  if (ruleName.startsWith('-moz') || ruleName.startsWith('-webkit')) {
    return
  }

  ruleValue = translateUnits(ruleValue)

  if (ruleValue.includes('CenturyGothic')) {
    ruleValue = 'CenturyGothic'
  }

  if (elm.tagName === 'UL' || elm.tagName === 'OL') {
    if (ruleName.includes('padding')) {
      ruleName = 'margin-left'
      ruleValue = '20px'
    } else if (ruleName.includes('margin')) {
      return
    }
  }

  if (elm.tagName === 'IMG') {
    if (ruleName === 'width' && ruleValue === '0px') {
      ruleValue = '22px'
    }
    if (ruleName === 'height' && ruleValue === '0px') {
      ruleValue = '24px'
    }
  }

  if (ruleName === 'display' && ruleValue === 'flex') {
    return
  }

  if (cssVars) {
    Object.entries(cssVars).forEach(([k, v]) => {
      ruleValue = ruleValue.replace(k, v)
    })
  }

  return buildRule(ruleName, ruleValue)
}

export const submitProposal = async ({ id, asDraft, asQuotePdf, asQuoteWord, formValues, fromDecisionTree }) => {
  if (process.env.NODE_ENV !== 'development') {
    window.onbeforeunload = function () {
      return 'Saving your data. Closing this window now may lose some of your progress.'
    }
  }

  const cart = fromDecisionTree ? context.state.decisionTreeCart : context.state.cart
  const cartTotals = {
    totalPoints: 0,
    totalHours: 0,
  }
  cart.forEach((product) => {
    cartTotals.totalPoints += +context.utils.product.getPointPrice(product) || 0
    cartTotals.totalHours += getDuration(product) || 0
  })
  cartTotals.totalWeeks = Math.ceil(cartTotals.totalHours / 40)

  let updatedHtml = ''
  let proposalName = 'SOW:' + new Date().toISOString()

  const firstItem = cart[0]
  if (!asDraft) {
    const addOnParent = firstItem.usesAddOnItems
    let htmlGenerator
    let template
    if (addOnParent) {
      htmlGenerator = spBuildHtml
    } else {
      const row = firstItem.rows.find((r) => r.rowType === 'component:document-template')
      if (row) {
        const { templateId } = row.control
        ;({
          data: [template],
        } = await request({ url: `${context.constants.appHost}/api/v1/templates/search?id=${templateId}` }))
      }

      htmlGenerator = template ? ssBuildHtml : generateDefaultSOW
    }
    try {
      const { data: formTemplate } = await request({
        url: `${context.constants.host}/api/v1/forms/${firstItem.formTemplateId}`,
      })
      proposalName = formTemplate.name
    } catch (err) {
      console.error(err)
    }

    updatedHtml = await htmlGenerator({
      asQuotePdf,
      asQuoteWord,
      cart,
      cartTotals,
      formValues,
      template,
    })
  }

  if (asQuoteWord) {
    const parser = new window.DOMParser()
    const doc = parser.parseFromString(updatedHtml, 'text/html')
    // resolve images to b64 strings
    await Promise.all(
      Array.prototype.map.call(doc.querySelectorAll('img'), async (el) =>
        fetchImage(el.src).then((v) => {
          el.src = v
        }),
      ),
    )

    const container = document.createElement('div')
    container.setAttribute('style', 'opacity: 0; position: absolute; top: -9999px; left: -9999px;')
    container.innerHTML = doc.body.innerHTML
    document.body.appendChild(container)

    // Inline styles for all elems
    const stylesheet = doc.styleSheets?.[0] || doc.querySelector('style')?.sheet
    const cssVars = cssRulesToArray(stylesheet.rules.item(':root').style)
      .filter(([k, v]) => k && v)
      .map(([k, v]) => ({ [k]: v?.trim() }))
      .reduce((acc, v) => ({ ...acc, ...v }), {})
    container.querySelectorAll('*').forEach((elm) => {
      if (elm.tagName === 'BR') {
        return
      }
      let css = []
      const computedStyle = window.getComputedStyle(elm)
      for (const rule of stylesheet.cssRules) {
        if (rule.selectorText && elm.matches(rule.selectorText)) {
          css = css.concat(cssRulesToArray(rule.style))
        }
      }
      const styleRules = css
        .concat(cssRulesToArray(elm.style))
        .filter(([k, v]) => k && v)
        .map(([k, v]) => ({ [k.trim()]: v.trim() }))
        .reduce((acc, v) => ({ ...acc, ...v }), {})
      const styleText = Object.entries(styleRules)
        .map(([k, v]) => transformRule(k, v, elm, cssVars, styleRules, computedStyle))
        .filter(Boolean)
        .join(';')

      // Workaround for absolute improper positioning of this element
      if (elm.className === 'title-backdrop') {
        const computedStyleStr = Array.prototype.map
          .call(computedStyle, (ruleName) => {
            if (!computedStyle[ruleName]) {
              return ''
            }

            return transformRule(ruleName, computedStyle[ruleName], elm)
          })
          .filter(Boolean)
          .join(';')
        elm.style = computedStyleStr
        const insetParts = elm.style.inset.split(' ')
        const insetPartsStr = ['top', 'right', 'bottom', 'left'].map((k, i) => buildRule(k, insetParts[i])).join(';')
        elm.setAttribute('style', `${elm.getAttribute('style')}${insetPartsStr};text-decoration:none;`)
      } else {
        elm.style = styleText
      }
    })

    doc.body.innerHTML = container.innerHTML
    updatedHtml = doc.documentElement.outerHTML
    document.body.removeChild(container)
  }

  // Preview document html in new window
  if (process.env.NODE_ENV === 'development') {
    const h = URL.createObjectURL(new window.Blob([updatedHtml], { type: 'text/html' }))
    window.open(h, '_blank')
  }

  // if (process.env.NODE_ENV === 'development') {
  //   await request({
  //     url: `${context.constants.appHost}/api/v1/documents/export?format=docx:libreoffice`,
  //     method: 'POST',
  //     body: JSON.stringify({
  //       data: {
  //         html: updatedHtml,
  //       },
  //     }),
  //   })
  //     .then((data) => {
  //       const wordDoc = new window.Blob([data], { type: 'application/octet-stream' })
  //       const propName = proposalName

  //       const link = document.createElement('a')
  //       link.href = URL.createObjectURL(wordDoc)
  //       link.download = `${propName}.docx`
  //       link.click()
  //       link.remove()
  //     })
  //   return
  // }

  const expiry = new Date()
  expiry.setDate(expiry.getDate() + 60)
  await context.actions.generateProposal({
    id,
    asDraft,
    asQuotePdf,
    asQuoteWord,
    formValues,
    fromDecisionTree,
    html: updatedHtml,
    proposalName,
    noTagReplacement: true,
    settings: {
      value: cartTotals.totalPoints,
      valueUnit: 'points',
      clientName: `${formValues.company} - ${firstItem.usesAddOnItems ? firstItem.name : 'Success Service'}`,
      expiryDate: expiry.toISOString(),
      docTemplateName: cart[0].template?.name || 'Success Service Proposal',
      items: cart.map((item) => omit(item, 'allRows')),
    },
    ...(process.env.NODE_ENV === 'development' ? { callback: () => {} } : {}),
  })
  window.onbeforeunload = undefined
  updateContextState((state) => {
    state.cartCompany = ''
  })
}
